import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { CardTypeEnum } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import { useFetchCardDetails } from 'features/cardManagement/hooks/useFetchCardDetails';
import DisclosureBottomSheet from 'features/home/HomeScreen/components/DisclosureBottomSheet';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import Illustration from 'shared/components/vxl/Illustration/Illustration';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import HyperlinkXsText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkXSText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import TitleXLText from 'shared/components/vxl/typography/primary/title/TitleXLText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

const SpendBenefits = () => {
  const router = useRouter();
  const { sourceData, error } = useFetchCardDetails();

  useViewDidFocusScreenAnalyticsEvent({
    key: '/spend-benefits/onBoardingCashbackInformation',
  });

  const disclosureAnalytics = usePressableAnalytics({
    analyticsId: 'onboardingDisclosuresAccessed',
  });
  const seeOffersBtnAnalytics = usePressableAnalytics({
    analyticsId: 'nearbyOffersClicked',
  });
  const viewCardBtnAnalytics = usePressableAnalytics({
    analyticsId: 'viewCardClicked',
  });
  const activateBtnAnalytics = usePressableAnalytics({
    analyticsId: 'saveYourChangeClicked',
  });

  const debitCardId: string =
    sourceData?.cards?.find((card) => card?.type === CardTypeEnum.DebitCard)
      ?.id ?? '';
  const { t: translatedText } = useTranslation('spendBenefits');
  const disclosureBottomSheetRef = useRef<BottomSheetModal>(null);

  const handleBackButtonAction = () => {
    router.back();
  };

  const onDisclosureHyperlinkClicked = () => {
    disclosureAnalytics();
    disclosureBottomSheetRef.current?.present();
  };
  const rewardsBtnClick = () => {
    seeOffersBtnAnalytics();
    router.push('/rewards');
  };

  const debitCardBtnPress = () => {
    viewCardBtnAnalytics();
    if (debitCardId) {
      router.push(`/myvaro/card/${debitCardId}`);
    } else {
      logErrorWithContext('Debit card ID not found: ' + error, {
        featureArea: FeatureArea.ONBOARDING_CHECKLIST,
        responsibleTeam: ResponsibleTeam.CUSTOMER_ACQUISITION,
        severity: ErrorSeverity.LOW,
      });
    }
  };

  const activateBtnClick = () => {
    activateBtnAnalytics();
    router.push(`/accounts/automatic-savings`);
  };

  return (
    <View style={styles.outerContainer}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: translatedText('backText'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryOnPress={handleBackButtonAction}
            />
          ),
        }}
      />
      <ScrollView>
        <View style={styles.container}>
          <View style={styles.image}>
            <Illustration illustration="CARD_C" />
          </View>
          <View style={styles.title}>
            <TitleXLText>{translatedText('heading')}</TitleXLText>
            <BodyBaseText style={styles.description}>
              {translatedText('description')}
            </BodyBaseText>
          </View>
          <ListItem
            key={0}
            primaryCopy={translatedText('listItem1')}
            showsChevron={false}
            leadingAccessory={{
              id: '1',
              name: VXLComponentId.ICON,
              props: {
                iconKey: 'STORE',
                iconAccessibilityLabel: 'Store icon',
              },
            }}
          />
          <View style={styles.hairLine}>
            <HairLineDivider />
          </View>
          <ListItem
            key={1}
            primaryCopy={translatedText('listItem2')}
            showsChevron={false}
            leadingAccessory={{
              id: '1',
              name: VXLComponentId.ICON,
              props: {
                iconKey: 'CASHBACK',
                iconAccessibilityLabel: 'Cashback icon',
              },
            }}
          />
          <View style={styles.disclosureLink}>
            <HyperlinkXsText onPress={onDisclosureHyperlinkClicked}>
              {translatedText('disclosure')}
            </HyperlinkXsText>
          </View>
          <View style={styles.buttonWrapper}>
            <Button
              label={translatedText('rewardsBtnText')}
              onPress={rewardsBtnClick}
            />
          </View>
          <View style={styles.imageContainer}>
            <View style={styles.img2}>
              <Illustration illustration="CASH_B" />
            </View>
          </View>
          <TitleMdText style={styles.titleMd}>
            {translatedText('titleMd')}
          </TitleMdText>
          <BodyBaseText style={styles.cardDesc}>
            {translatedText('cardDesc')}
          </BodyBaseText>
          <View style={styles.cardBtnWrapper}>
            <Button
              type="SECONDARY"
              label={translatedText('cardBtnText')}
              onPress={debitCardBtnPress}
              width="HUG"
            />
          </View>
          <View style={styles.savingsSection}>
            <View style={styles.imageContainer2}>
              <View style={styles.img2}>
                <Illustration illustration="CASH_E" />
              </View>
            </View>
            <TitleMdText style={styles.titleMd}>
              {translatedText('activateTitle')}
            </TitleMdText>
            <BodyBaseText style={styles.cardDesc}>
              {translatedText('activateDesc')}
            </BodyBaseText>
            <View style={styles.activateBtnWrapper}>
              <Button
                type="SECONDARY"
                label={translatedText('activateBtnText')}
                onPress={activateBtnClick}
                width="HUG"
              />
            </View>
          </View>
          <DisclosureBottomSheet
            isDisclosurePresented={false}
            disclosureData={translatedText('isCashBackText')}
            bottomSheetRef={disclosureBottomSheetRef}
          />
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  activateBtnWrapper: {
    alignItems: 'center',
    marginTop: theme.spacing.l,
  },
  buttonWrapper: {
    alignSelf: 'center',
    paddingTop: theme.spacing.m,
    marginBottom: theme.spacing.m,
  },
  cardBtnWrapper: {
    alignItems: 'center',
    marginTop: theme.spacing.l,
    marginBottom: theme.spacing.l,
  },
  cardDesc: {
    alignSelf: 'center',
    marginHorizontal: theme.spacing.m,
    marginBottom: 0,
    marginTop: theme.spacing.s,
    textAlign: 'center',
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    flexDirection: 'column',
    flexGrow: 1,
    zIndex: 1,
  },
  description: {
    marginTop: theme.spacing.xl,
  },
  disclosureLink: {
    marginLeft: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  hairLine: {
    marginHorizontal: theme.spacing.m,
  },
  image: {
    alignItems: 'center',
    alignSelf: 'stretch',
    display: 'flex',
    gap: 10,
    height: 200,
    justifyContent: 'center',
    padding: theme.spacing.m,
    marginBottom: theme.spacing.m,
  },
  imageContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.expressive.orchid,
    borderColor: theme.color.expressive.orchid,
    display: 'flex',
    height: 'auto',
    justifyContent: 'center',
    marginHorizontal: theme.spacing.m,
    marginBottom: theme.spacing.l,
    marginTop: theme.spacing.l,
    padding: theme.spacing.l,
  },
  imageContainer2: {
    alignItems: 'center',
    backgroundColor: theme.color.expressive.mint,
    borderColor: theme.color.expressive.mint,
    display: 'flex',
    height: 'auto',
    justifyContent: 'center',
    marginHorizontal: theme.spacing.m,
    marginTop: theme.spacing.l,
    marginBottom: theme.spacing.l,
    padding: theme.spacing.l,
  },
  img2: {
    alignItems: 'center',
    aspectRatio: 1,
    display: 'flex',
    height: 96,
    justifyContent: 'center',
    width: 96,
  },
  outerContainer: {
    backgroundColor: theme.color.background.tertiary,
    flex: 1,
  },
  savingsSection: {
    marginBottom: theme.spacing.xxl,
  },
  title: {
    alignSelf: 'stretch',
    display: 'flex',
    margin: 0,
    padding: theme.spacing.m,
    paddingBottom: 0,
  },
  titleMd: {
    alignSelf: 'center',
  },
});

export default SpendBenefits;
